import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * update 2026_07_13.1
 *
 * Wraps the "Feedback" area on the OpenScoreCard / InfoWindow. Hovering,
 * touching or clicking the area shows a pop-up explaining where the feedback
 * came from (with a link to Digital Reception), with the care home's
 * feedback_summary underneath.
 *
 * Hover shows the pop-up and moving away hides it (a short close delay lets
 * the pointer travel from the trigger into the pop-up). Click / touch pins it
 * open until the close button, an outside click/tap, or Escape closes it.
 * Rendered through a portal with fixed positioning so it is never clipped by
 * the card or the Google Maps info window.
 */

const POPUP_WIDTH = 300;
const CLOSE_DELAY_MS = 150;

interface FeedbackPopupProps {
    /** The care home's feedback_summary (the DigitalFeedback field). */
    summary?: string;
    /** Styling for the trigger wrapper so it can sit in either card layout. */
    className?: string;
    style?: React.CSSProperties;
    children: React.ReactNode;
}

const FeedbackPopup: React.FC<FeedbackPopupProps> = ({ summary, className, style, children }) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const closeTimerRef = useRef<number | null>(null);
    const [open, setOpen] = useState(false);
    const [pinned, setPinned] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number; above: boolean }>({
        top: 0,
        left: 0,
        above: false
    });

    const cancelScheduledClose = () => {
        if (closeTimerRef.current !== null) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const openPopup = () => {
        cancelScheduledClose();
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (rect) {
            const left = Math.min(
                Math.max(8, rect.left + rect.width / 2 - POPUP_WIDTH / 2),
                Math.max(8, window.innerWidth - POPUP_WIDTH - 8)
            );
            // Flip above the trigger when there is little room below it.
            const above = rect.bottom + 220 > window.innerHeight && rect.top > 220;
            setPosition({ top: above ? rect.top - 8 : rect.bottom + 8, left, above });
        }
        setOpen(true);
    };

    const scheduleClose = () => {
        if (pinned) return;
        cancelScheduledClose();
        closeTimerRef.current = window.setTimeout(() => {
            setOpen(false);
            closeTimerRef.current = null;
        }, CLOSE_DELAY_MS);
    };

    const closePopup = () => {
        cancelScheduledClose();
        setPinned(false);
        setOpen(false);
    };

    // Escape closes; while pinned, an outside click/tap closes.
    useEffect(() => {
        if (!open) return;

        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closePopup();
        };
        const handleOutside = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node;
            if (wrapperRef.current?.contains(target)) return;
            if (popupRef.current?.contains(target)) return;
            closePopup();
        };

        document.addEventListener('keydown', handleKey);
        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside);
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchstart', handleOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Clear any pending close timer on unmount.
    useEffect(() => () => cancelScheduledClose(), []);

    const popupStyle: React.CSSProperties = {
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: position.above ? 'translateY(-100%)' : undefined,
        zIndex: 10000,
        width: `${POPUP_WIDTH}px`,
        maxWidth: 'calc(100vw - 16px)',
        borderRadius: '12px',
        border: '1px solid rgba(2, 81, 80, 0.16)',
        background: '#ffffff',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.18)',
        padding: '0.7rem 0.8rem',
        fontFamily: '"Trebuchet MS", sans-serif',
        color: '#4f5960',
        lineHeight: 1.4,
        textAlign: 'left',
        cursor: 'default'
    };

    const explanationStyle: React.CSSProperties = {
        margin: 0,
        paddingRight: '1.2rem',
        fontSize: '0.72rem'
    };

    const linkStyle: React.CSSProperties = {
        color: '#025150',
        fontWeight: 700,
        textDecoration: 'underline'
    };

    const dividerStyle: React.CSSProperties = {
        height: '1px',
        margin: '0.5rem 0',
        background: '#e0e2e4'
    };

    const summaryStyle: React.CSSProperties = {
        margin: 0,
        fontSize: '0.74rem',
        fontWeight: 600,
        color: '#0d5f57'
    };

    const closeBtnStyle: React.CSSProperties = {
        position: 'absolute',
        top: '4px',
        right: '6px',
        width: 'auto',
        padding: '0 0.2rem',
        background: 'none',
        border: 'none',
        color: '#727982',
        fontSize: '1rem',
        lineHeight: 1,
        cursor: 'pointer'
    };

    return (
        <div
            ref={wrapperRef}
            className={className}
            style={{ ...style, cursor: 'help' }}
            onMouseEnter={openPopup}
            onMouseLeave={scheduleClose}
            onClick={(e) => {
                // Cards can be click-to-select; the feedback area must not trigger that.
                e.stopPropagation();
                setPinned(true);
                openPopup();
            }}
        >
            {children}
            {open && createPortal(
                <div
                    ref={popupRef}
                    role="tooltip"
                    style={popupStyle}
                    onMouseEnter={cancelScheduledClose}
                    onMouseLeave={scheduleClose}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button type="button" style={closeBtnStyle} onClick={closePopup} aria-label="Close">&times;</button>
                    <p style={explanationStyle}>
                        This is the last weeks automated feedback from visitors after they have
                        been to the carehome that is collected by{' '}
                        <a
                            href="https://www.dreception.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={linkStyle}
                        >
                            Digital Reception
                        </a>
                    </p>
                    {summary && (
                        <>
                            <div style={dividerStyle} />
                            <p style={summaryStyle}>{summary}</p>
                        </>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

export default FeedbackPopup;
